import React, { useState } from "react";
import axios from "axios";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Input, Toast } from "../ui";
import { appConstants } from "../_constants/app.constants";
import { useAuth } from "./AuthContext";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from?.pathname || "/mis-torneos";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setNeedsConfirmation(false);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const code = err.response?.data?.code || err.response?.data?.Code;
      const msg =
        err.response?.data?.message ||
        err.response?.data?.Message ||
        "No se pudo iniciar sesión. Revisa email y contraseña.";
      if (code === "SIGN_IN_NOT_ALLOWED") {
        // Backend returns this when the email is not yet confirmed. Show a
        // friendly message + a button to resend the confirmation link.
        setError("Tu cuenta aún no está confirmada. Revisa tu correo y pulsa el enlace que te enviamos.");
        setNeedsConfirmation(true);
      } else {
        setError(typeof msg === "string" ? msg : "Error de autenticación");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setError("Escribe tu email arriba y vuelve a pulsar reenviar.");
      return;
    }
    setResending(true);
    setInfo("");
    try {
      await axios.post(`${appConstants.API_URL}/api/auth/resend-confirmation`, { email });
      setInfo("Si la cuenta existe y aún no está confirmada, te enviamos un nuevo enlace.");
    } catch {
      setInfo("Si la cuenta existe y aún no está confirmada, te enviamos un nuevo enlace.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-cream via-white to-cream-deep px-4 py-12">
      <div className="pointer-events-none absolute inset-0 bg-noise opacity-30 mix-blend-multiply" aria-hidden />
      <div className="relative z-10 mx-auto flex min-h-[80vh] w-full max-w-md items-center">
        <Card className="w-full overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-gold via-gold-bright to-arena-bright" />
          <CardHeader className="pb-1 pt-6">
            <h1 className="font-display text-3xl font-extrabold text-ink">Iniciar sesión</h1>
            <p className="mt-1 text-sm text-ink-muted">Accede con tu cuenta para gestionar torneos.</p>
          </CardHeader>
          <CardBody className="pb-6">
            {error ? <Toast kind="error" className="mb-4">{error}</Toast> : null}
            {info ? <Toast kind="info" className="mb-4">{info}</Toast> : null}
            {needsConfirmation ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mb-3 w-full"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? "Reenviando…" : "Reenviar email de confirmación"}
              </Button>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                label="Contraseña"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="text-right">
                <RouterLink
                  to="/forgot-password"
                  className="text-sm font-semibold text-arena-bright hover:underline"
                >
                  ¿Has olvidado tu contraseña?
                </RouterLink>
              </div>
              <Button type="submit" variant="primary" size="lg" className="mt-3 w-full">
                {submitting ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            <div className="mt-4 space-y-1 text-sm">
              <p>
                ¿No tienes cuenta?{" "}
                <RouterLink to="/register" className="font-semibold text-arena-bright hover:underline">
                  Registrarse
                </RouterLink>
              </p>
              <RouterLink to="/" className="font-semibold text-arena-bright hover:underline">
                Volver al inicio
              </RouterLink>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
